import {
  conversationMessagesQueryOptions,
  conversationQuery as conversationQueryOptions,
  useMarkMemberMessageSeenMutation,
} from "@/api/messages";
import { queryClient } from "@/api/utils";
import { InfiniteScrollEnd } from "@/components/InfiniteScrollEnd";
import { PageInset } from "@/components/PageInset";
import { seo } from "@/utils/seo";
import { locale } from "@common/utils/i18n";
import { Avatar, Box, Flex, QueryHandler, Spinner, Text } from "@givenwell/components";
import { ConversationMessage } from "@givenwell/marketplace-api";
import { colors } from "@givenwell/theme";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Message } from "./Message";
import { NewMessageBar } from "./NewMessageBar";
import { TopBar } from "./TopBar";

export const Route = createFileRoute("/_auth/_app/messages/$conversationId")({
  component: ConversationPage,
  head: async ({ params }) => {
    const conversation = await queryClient.ensureQueryData(conversationQueryOptions(params.conversationId));
    return { meta: seo({ title: conversation?.supplier_name || "Messages" }) };
  },
});

const route = getRouteApi("/_auth/_app/messages/$conversationId");

function ConversationPage() {
  const { conversationId } = route.useParams();

  const conversationQuery = useQuery(conversationQueryOptions(conversationId));
  const messagesQuery = useInfiniteQuery(conversationMessagesQueryOptions(conversationId));
  const [tick, setTick] = useState(0);

  const scrollContainerRef = useRef<HTMLDivElement>(null!);

  const messages = messagesQuery.data?.pages.flat() ?? [];
  const lastMessageId = messages[messages.length - 1]?.id;
  const prevLastMessageIdRef = useRef<string | undefined>(undefined);

  useLayoutEffect(() => {
    // Scroll to bottom on first load
    if (messagesQuery.status === "success") {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [messagesQuery.status]);

  // Handle scrolling when new messages arrive via socket
  useEffect(() => {
    if (!lastMessageId || lastMessageId === prevLastMessageIdRef.current) {
      return;
    }

    // New message arrived
    if (prevLastMessageIdRef.current !== undefined && scrollContainerRef.current) {
      const isNearTheBottom = getIsNearTheBottom(scrollContainerRef.current);
      if (isNearTheBottom) {
        flushSync(() => {
          setTick(t => t + 1);
        });
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    }

    prevLastMessageIdRef.current = lastMessageId;
  }, [lastMessageId]);

  async function fetchPreviousPage() {
    if (!messagesQuery.hasPreviousPage) return;
    await messagesQuery.fetchPreviousPage();

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const oldHeight = scrollContainer.scrollHeight;
      flushSync(() => {
        setTick(tick + 1);
      });
      const newHeight = scrollContainer.scrollHeight;

      scrollContainer.scrollTop = scrollContainer.scrollTop + (newHeight - oldHeight);
    }
  }

  const markSeenMutation = useMarkMemberMessageSeenMutation();

  function markSeenIfNecessary() {
    const messages = messagesQuery.data?.pages.flat() ?? [];
    if (!messages.length) return;
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) return;
    if (!lastMessage.seen && markSeenMutation.status !== "pending") {
      markSeenMutation.mutate(lastMessage.id);
    }
  }

  function scrollToBottom() {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
      });
    }
  }

  return (
    <Flex
      css={{
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        pt: "var(--safe-area-inset-top)",
      }}
    >
      <TopBar conversation={conversationQuery.data} />
      <Flex
        css={{
          flex: "1 0 0px",
          flexDir: "column",
          overflow: "hidden",
        }}
      >
        <Flex css={{ flexDir: "column", flex: "1 0 0px", overflow: "auto" }} ref={scrollContainerRef}>
          <QueryHandler
            query={conversationQuery}
            success={({ data }) =>
              data ? (
                <>
                  <QueryHandler
                    query={messagesQuery}
                    success={({ data: infiniteData }) => {
                      const groupedMessages = groupByDay(infiniteData.pages.flat());
                      return (
                        <PageInset css={{ flexGrow: 1, pb: 20 }}>
                          {messagesQuery.hasPreviousPage ? (
                            <InfiniteScrollEnd onReach={() => fetchPreviousPage()} style={{ height: 256 }}>
                              {messagesQuery.isFetchingPreviousPage && <Spinner />}
                            </InfiniteScrollEnd>
                          ) : (
                            <Flex
                              css={{
                                flexDirection: "column",
                                gap: 16,
                                items: "center",
                                align: "center",
                                pt: 120,
                              }}
                            >
                              <Avatar
                                size={50}
                                id={data.supplier_id}
                                name={data.supplier_name}
                                src={data.supplier_profile_url || undefined}
                              />
                              <Box css={{ spaceY: 4 }}>
                                <Text css={{ fontScale: "sm" }}>{data.supplier_name}</Text>
                                <Text
                                  css={{
                                    color: colors.text2,
                                    fontScale: "xs",
                                    pb: 8,
                                    textWrap: "balance",
                                    px: 16,
                                  }}
                                >
                                  This is the beginning of your message history with {data.supplier_name}
                                </Text>
                              </Box>
                            </Flex>
                          )}
                          {groupedMessages.map((day, i) => (
                            <Fragment key={i}>
                              <DayMarker date={day.date} />
                              {day.messages.map(message => (
                                <Message key={message.id} message={message} conversation={data} />
                              ))}
                            </Fragment>
                          ))}
                          <InfiniteScrollEnd onReach={markSeenIfNecessary} />
                        </PageInset>
                      );
                    }}
                  />
                </>
              ) : null
            }
          />
        </Flex>
      </Flex>
      <NewMessageBar
        onSubmit={() =>
          messagesQuery.fetchNextPage().then(() => {
            flushSync(() => {
              setTick(t => t + 1);
            });
            scrollToBottom();
          })
        }
      />
    </Flex>
  );
}

function groupByDay(messages: ConversationMessage[]) {
  const days: Array<{ date: Date; messages: ConversationMessage[] }> = [];
  if (!messages.length) return days;

  let previousDay = new Date(0);

  for (const message of messages) {
    const messageDay = new Date(message.sent);
    if (
      messageDay.getDate() !== previousDay.getDate() ||
      messageDay.getMonth() !== previousDay.getMonth() ||
      messageDay.getFullYear() !== previousDay.getFullYear()
    ) {
      days.push({ date: messageDay, messages: [] });
      previousDay = messageDay;
    }
    days[days.length - 1]!.messages.push(message);
  }

  return days;
}

function getIsNearTheBottom(element: HTMLElement) {
  const { scrollHeight, scrollTop, clientHeight } = element;

  const actualBottom = scrollHeight;
  const visibleBottom = clientHeight + scrollTop;
  return actualBottom - visibleBottom < 100;
}

function DayMarker({ date }: { date: Date }) {
  return (
    <Flex css={{ justify: "center", items: "center", py: 12 }}>
      <Box
        css={{
          backgroundColor: "white",
          px: 12,
          py: 6,
          rounded: 9999,
          fontScale: "xs",
          color: colors.text2,
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        {formatDay(date)}
      </Box>
    </Flex>
  );
}

function formatDay(date: Date) {
  const today = new Date();
  if (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  ) {
    return "Today";
  }
  if (date.getFullYear() === today.getFullYear()) {
    return date.toLocaleDateString(locale, { month: "long", day: "numeric" });
  }
  return date.toLocaleDateString(locale, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}
